import * as React from 'react';

import { cva, VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const inputVariants = cva(
  'outline-0 flex h-12 w-full px-4 py-1 text-[16px] placeholder:text-muted-foreground focus-visible:shadow-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'rounded-full bg-[#F5F5F6] border-none',
        secondary: 'border-b border-[#E4E4E4] bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
);
export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  inputClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, inputClassName, variant, type, leftIcon, rightIcon, ...props },
    ref,
  ) => {
    const handleKeyDown = React.useMemo(
      () =>
        type === 'number'
          ? (event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'e' || event.key === 'E') {
                event.preventDefault();
              }
            }
          : undefined,
      [type],
    );

    const handleInput = React.useMemo(
      () =>
        type === 'number'
          ? (event: React.ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value;
              if (/[eE]/.test(value)) {
                event.target.value = value.replace(/[eE]/g, '');
              }
            }
          : undefined,
      [type],
    );

    return (
      <div className={cn('relative flex items-center', className)}>
        {leftIcon && (
          <div className="absolute left-3 flex items-center">{leftIcon}</div>
        )}
        <input
          type={type}
          className={cn(
            inputVariants({ variant }),
            leftIcon ? 'pl-10' : '',
            rightIcon ? 'pr-10' : '',
            inputClassName,
          )}
          ref={ref}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          {...props}
        />
        {rightIcon && (
          <div className="absolute right-3 flex items-center">{rightIcon}</div>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
